import type { UseFormRegister, FieldErrors } from 'react-hook-form';
import { FormField, Input, Textarea } from '@shared/ui';
import { CATEGORY_KINDS, type CategoryKind } from '@entities/taxonomy';
import type { UpdateProfileRequest } from '@entities/manufacturer';
import { CategoryPicker } from './category-picker';

const KIND_META: Record<CategoryKind, { label: string; hint: string }> = {
  PRODUCER: { label: 'Категории производителя', hint: 'Что компания умеет производить' },
  ECO_PRODUCER: { label: 'Эко-категории', hint: 'Направления эко-производства' },
  PRODUCT: { label: 'Товарные категории', hint: 'По ним вендор получает RFQ в свой feed' },
};

export type ProfileValues = Omit<
  UpdateProfileRequest,
  'certificateMediaIds' | 'ecoCertificateMediaIds' | 'logoMediaId' | 'videoMediaId' | 'categories'
>;

/** Shared block for wizard step-2 and the detail edit dialog. */
export function ProfileFields<T extends ProfileValues>({
  register,
  errors,
  selectedCategoryIds,
  onCategoriesChange,
}: {
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  selectedCategoryIds: string[];
  onCategoriesChange: (ids: string[]) => void;
}) {
  const r = register as unknown as UseFormRegister<ProfileValues>;
  const e = errors as unknown as FieldErrors<ProfileValues>;

  return (
    <div className="space-y-4">
      <FormField label="Название компании *" htmlFor="companyName" error={e.companyName?.message}>
        <Input id="companyName" {...r('companyName')} />
      </FormField>

      <div className="grid grid-cols-3 gap-3">
        <FormField label="Страна (ISO-2)" htmlFor="country" error={e.country?.message}>
          <Input id="country" maxLength={2} {...r('country')} placeholder="UZ" />
        </FormField>
        <FormField label="Год основания" htmlFor="foundedYear" error={e.foundedYear?.message}>
          <Input id="foundedYear" type="number" {...r('foundedYear')} />
        </FormField>
        <FormField
          label="Размер компании"
          htmlFor="employeesRange"
          error={e.employeesRange?.message}
        >
          <select
            id="employeesRange"
            {...r('employeesRange')}
            className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="">—</option>
            <option value="S">S (до 50)</option>
            <option value="M">M (50–250)</option>
            <option value="L">L (250–1000)</option>
            <option value="XL">XL (1000+)</option>
          </select>
        </FormField>
      </div>

      <FormField label="Сайт" htmlFor="website" error={e.website?.message}>
        <Input id="website" placeholder="https://…" {...r('website')} />
      </FormField>

      <FormField label="Описание" htmlFor="description" error={e.description?.message}>
        <Textarea id="description" rows={3} {...r('description')} />
      </FormField>

      <label className="flex w-fit cursor-pointer items-center gap-2 text-sm">
        <input type="checkbox" {...r('ecoProducer')} className="h-4 w-4" />
        Эко-производитель
      </label>

      <div className="space-y-2">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Категории
        </div>
        {CATEGORY_KINDS.map((kind) => (
          <CategoryPicker
            key={kind}
            kind={kind}
            label={KIND_META[kind].label}
            hint={KIND_META[kind].hint}
            selected={selectedCategoryIds}
            onChange={onCategoriesChange}
          />
        ))}
      </div>
    </div>
  );
}
